import { useEffect, useState } from 'react';
import { API_BASE, AUTH_TOKEN, authUser, COME_UP } from '../config';

// useStoryAccess — is Stage One open for THIS account?
//
// The story gates used to be client-side `isAdmin` checks, which made the
// intro untestable by exactly the account that matters most: a fresh,
// non-admin user with a bare avatar. The truth lives server-side
// (WORLD_STORY_USER_IDS allowlist, storyAccess.js — fails closed in
// production, open in development), and /api/dialogue/access is the probe
// registered before the gate so asking doesn't 404.
//
// Admins short-circuit to open so the building loop never flickers; the
// server still enforces the real gate on every story call regardless of what
// any client believes.

let cached = null;            // module-level: one probe per page load
let inflight = null;

async function probe() {
  if (cached !== null) return cached;
  if (!inflight) {
    inflight = fetch(`${API_BASE}/api/dialogue/access`, {
      headers: AUTH_TOKEN ? { Authorization: `Bearer ${AUTH_TOKEN}` } : {},
    })
      .then(async (r) => {
        // Say WHY out loud — a silently hidden phone cost a live test session.
        if (r.status === 404) {
          console.warn('[story] /api/dialogue/access is 404 — the API predates the probe. RESTART THE API.');
          cached = false; return false;
        }
        const d = await r.json().catch(() => ({}));
        cached = !!d?.open;
        // THE DEAD ZONE, healed: we SENT a token but the server saw an
        // anonymous caller — the saved token is invalid/expired. The client
        // half still believes it's a member (IS_GUEST is false), so neither
        // the guest call nor the member quests can ring the phone. No real
        // player will type a rescue param (Khari), so the world fixes itself:
        // drop the dead token and reload ONCE into the true guest flow. The
        // sessionStorage latch stops a loop if the token comes from somewhere
        // we can't clear (VITE_AUTH_TOKEN).
        if (AUTH_TOKEN && d?.guest === true) {
          let dropped = false;
          try { dropped = sessionStorage.getItem('world:tokenDropped') === '1'; } catch { /* noop */ }
          if (!dropped) {
            console.warn('[story] saved token is INVALID/EXPIRED — dropping it and reloading as a guest.');
            try {
              sessionStorage.setItem('world:tokenDropped', '1');
              localStorage.removeItem('world:authToken');
            } catch { /* noop */ }
            window.location.reload();
            return false;
          }
          console.warn('[story] token still present after a drop (VITE_AUTH_TOKEN?) — story cannot start for this identity. Use ?asguest=1 or a fresh ?token=.');
        }
        // A valid member session re-arms the healer for the NEXT expiry.
        if (d?.guest === false) { try { sessionStorage.removeItem('world:tokenDropped'); } catch { /* noop */ } }
        console.info(`[story] access probe: ${cached ? 'OPEN' : 'CLOSED for this account'}${cached ? '' : ' — add this user id to WORLD_STORY_USER_IDS (or run the API in development mode)'}`);
        return cached;
      })
      .catch((e) => {
        console.warn('[story] access probe failed:', e?.message || e);
        cached = false; return false;
      });
  }
  return inflight;
}

export function useStoryAccess() {
  const { isAdmin } = authUser();
  const [open, setOpen] = useState(isAdmin || cached === true);
  useEffect(() => {
    if (isAdmin || !COME_UP) return undefined;
    let dead = false;
    probe().then((v) => { if (!dead) setOpen(v); });
    return () => { dead = true; };
  }, [isAdmin]);
  // Hidden for everyone, admins included, while the come-up waits on its
  // rewrite (config.COME_UP) — Lyra ringing 15s after spawn is the come-up.
  if (!COME_UP) return false;
  return isAdmin || open;
}

export default useStoryAccess;
